import React, {memo, useEffect, useRef, useState} from "react";
import {CircularProgress, IconButton, Stack, useTheme} from "@mui/material";
import FaIcon from "@/components/utilities/fa-icon";
import {useAppDispatch} from "@/redux/hooks";
import {setAllowBottomNav} from "@/redux/layout.slice";
import useProductCount from "@/hooks/useProductCount";
import {Field} from "@/components/form";
import {PurchasingControlProps} from "@/types/product";
import Box from "@mui/material/Box";

const useFocus = (): [React.RefObject<HTMLInputElement>, () => void] => {
    const htmlElRef = useRef<HTMLInputElement>(null);
    const setFocus = () => {
        htmlElRef.current?.focus();
    };
    return [htmlElRef, setFocus];
};

const COMMIT_DELAY_MS = 1500;
const LOADING_INDICATOR_DELAY_MS = 180;

const PurchasingControl: React.FC<PurchasingControlProps> = ({
                                                                 id,
                                                                 productName,
                                                                 preOrder = false,
                                                                 isBigButton,
                                                                 isCart,
                                                                 initialCount
                                                             }) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const [inputRef] = useFocus();

    const [animate, setAnimate] = useState(false);
    const [trashAnimate, setTrashAnimate] = useState(false);
    const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);

    const shouldRefocusRef = useRef(false);
    const isEditingRef = useRef(false);
    const lastCommittedRef = useRef<number | null>(null);
    const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const initial = typeof initialCount === "number" ? initialCount : 0;
    const initialDraft = isCart ? "" : (initial ? String(initial) : "");
    const [draft, setDraft] = useState<string>(initialDraft);

    const {productCount, setProductCount, isLoading: countLoading} = useProductCount(
        id,
        productName,
        preOrder ? "preorder" : "cart"
    );

    useEffect(() => {
        if (isEditingRef.current) return;

        const count = typeof productCount === "number" ? productCount : (typeof initialCount === "number" ? initialCount : 0);

        const nextValue = count > 0 ? String(count) : "";

        setDraft(nextValue);

        setAnimate(true);
        const t = setTimeout(() => setAnimate(false), 300);
        return () => clearTimeout(t);
    }, [productCount, initialCount]);

    useEffect(() => {
        return () => {
            if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
        };
    }, []);

    useEffect(() => {
        if (!shouldRefocusRef.current) return;

        if (!countLoading) {
            inputRef.current?.focus();
        }

        shouldRefocusRef.current = false;
    }, [countLoading, inputRef]);

    useEffect(() => {
        if (!countLoading) {
            setShowLoadingIndicator(false);
            return;
        }

        const timer = setTimeout(
            () => setShowLoadingIndicator(true),
            LOADING_INDICATOR_DELAY_MS
        );

        return () => clearTimeout(timer);
    }, [countLoading]);

    const parseDraftToNumber = (value: string) => {
        const trimmed = value.trim();
        if (trimmed === "") return 0;
        const n = Number(trimmed);
        if (Number.isNaN(n)) return null;
        return n;
    };

    const scheduleCommit = (nextDraft: string) => {
        setDraft(nextDraft);
        shouldRefocusRef.current = true;

        /* if (isEditingRef.current) {
             if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
             return;
         }
 */
        if (isCart && nextDraft.trim() === "") {
            if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
            return;
        }

        if (commitTimerRef.current) clearTimeout(commitTimerRef.current);

        commitTimerRef.current = setTimeout(() => {
            const numeric = parseDraftToNumber(nextDraft);
            if (numeric === null) return;

            if (lastCommittedRef.current === numeric) return;
            lastCommittedRef.current = numeric;

            setProductCount(numeric, !isCart);
        }, COMMIT_DELAY_MS);
    };

    const commitNow = () => {
        if (commitTimerRef.current) clearTimeout(commitTimerRef.current);

        if (isCart && draft.trim() === "") return;

        const numeric = parseDraftToNumber(draft);
        if (numeric === null) return;

        if (lastCommittedRef.current !== numeric) {
            lastCommittedRef.current = numeric;
            setProductCount(numeric, !isCart);
        }
    };

    const commitImmediateValue = (value: number) => {
        setDraft(String(value));
        if (commitTimerRef.current) clearTimeout(commitTimerRef.current);

        lastCommittedRef.current = value;
        setProductCount(value, !isCart);
    };

    const inCommonIconButtonStyles = {
        width: {md: isBigButton ? 34 : 32, xs: isBigButton ? 30 : 30},
        minWidth: "unset",
        borderRadius: 0,
        transition: "all 0.2s ease",
        transform: "scale(1)",
        "&:hover": {
            transform: "scale(1.06)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
        },
        "&:active": {transform: "scale(0.95)"},
        "&.Mui-disabled": {
            opacity: 0.45,
        },
    };

    const currentNumeric = parseDraftToNumber(draft) ?? 0;

    return (
        <Stack
            direction = "row"
            justifyContent = "end"
            alignItems = "center"
            position = "relative"
            // p={.35}
            borderRadius = {2}
            // bgcolor={theme.palette.grey[50]}
            // border={`1px solid ${theme.palette.grey[300]}`}
            sx = {{
                direction: "ltr",
            }}
        >
            <IconButton
                onClick = {() => {
                    setTrashAnimate(true);
                    commitImmediateValue(0);
                    setTimeout(() => setTrashAnimate(false), 300);
                }}
                aria-label = "delete"
                sx = {{
                    ...inCommonIconButtonStyles,
                    // bgcolor: theme.palette.grey[200],
                    borderTopLeftRadius: 3,
                    borderBottomLeftRadius: 3,
                    "&:disabled": {
                        // bgcolor: theme.palette.grey[200],
                        opacity: .5

                    },

                    "&:hover": {
                        transform: "scale(1.06)",
                        "& svg, & i": {
                            // color: currentNumeric > 0 ? `${theme.palette.error.main} !important` : undefined,
                        },
                    },
                }}
                disabled = {countLoading || currentNumeric <= 0}

            >
                <FaIcon
                    faIcon = "fa-trash-alt"
                    variant = "regular"
                    fontSize = {{md: isBigButton ? 26 : 22, xs: isBigButton ? 26 : 22}}
                    lighter
                    sx = {{
                        mr: 1,
                        transition: "transform 0.3s ease, color 0.2s ease",
                        transform: trashAnimate ? "rotate(-20deg)" : "rotate(0deg)",
                        color: !(countLoading || currentNumeric <= 0) ? theme.palette.primary.dark : theme.palette.primary.light,
                    }}
                />
            </IconButton >

            <Box
                border = {`1.6px solid ${preOrder ? theme.palette.secondary.dark : productCount > 0 ? theme.palette.primary.dark : theme.palette.primary.button}`}
                display = "flex"
                alignItems = "stretch"
                overflow = "hidden"
                borderRadius = {2}

            >
                <Box
                    position = "relative"
                    overflow = "hidden"
                    height = "100%"
                    bgcolor = {theme.palette.background.paper}
                    sx = {{
                        // borderRadius: 4,
                        transition: "all 0.2s ease",


                    }}
                >
                    <Field.Text
                        sx = {{
                            "& .MuiInputBase-root": {
                                width: {md: (isBigButton) ? 56 : 44, xs: (isBigButton) ? 58 : 42},
                                position: "relative",
                                borderRadius: 0,
                                overflow: "hidden",
                                bgcolor: productCount > 0 ? "transparent" : theme.palette.background.default,
                                transform: animate ? "scale(1.08)" : "scale(1)",
                                transition: "transform 0.25s ease-in-out",
                                height: {md: 37, xs: isBigButton ? 38 : 34},

                            },
                            "& input": {
                                px: 0,
                                height: "100%",


                            },
                        }}
                        size = "small"
                        type = "tel"
                        inputMode = "numeric"
                        ref = {inputRef}
                        value = {draft}
                        autoComplete = "off"
                        normalizeDigits
                        noPadding
                        borderless
                        onFocus = {() => {
                            isEditingRef.current = true;
                            dispatch(setAllowBottomNav(false));

                            if (draft === "0") setDraft("");
                        }}

                        onBlur = {() => {
                            dispatch(setAllowBottomNav(true));
                            isEditingRef.current = false;

                            if (isCart && draft.trim() === "") {
                                commitImmediateValue(0);
                                return;
                            }

                            commitNow();
                        }}

                        onChange = {(e) => {
                            const raw = e.target.value;
                            if (raw !== "" && !/^\d+$/.test(raw)) return;
                            scheduleCommit(raw);
                        }}

                        onKeyDown = {(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                commitNow();
                                (e.currentTarget as HTMLInputElement).blur();
                                return;
                            }

                            if (e.key === "+" || e.key === "=") {
                                e.preventDefault();
                                const base = parseDraftToNumber(draft) ?? 0;
                                scheduleCommit(String(base + 1));
                                return;
                            }

                            if (e.key === "-") {
                                e.preventDefault();
                                const base = parseDraftToNumber(draft) ?? 0;
                                scheduleCommit(String(Math.max(0, base - 1)));
                                return;
                            }
                        }}
                        inputProps = {{
                            readOnly: countLoading,
                            dir: "ltr",
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            style: {textAlign: "center"},
                            sx: {

                                textAlign: "center",
                                fontWeight: 700,
                                fontSize: isBigButton ? 22 : 18,
                                opacity: countLoading ? 0.65 : 1,
                                transition: "opacity 0.2s ease",
                            },
                        }}
                    />

                </Box >

                <IconButton
                    onClick = {() => commitImmediateValue(currentNumeric + 1)}
                    aria-label = "add"
                    aria-busy = {countLoading}
                    sx = {{
                        ...inCommonIconButtonStyles,
                        height: "100%",
                        alignSelf: "stretch",
                        background: preOrder
                            ? productCount > 0 ? theme.palette.secondary.dark : theme.palette.secondary.main
                            : productCount > 0
                                ? theme.palette.primary.dark
                                : theme.palette.primary.button,

                        color: theme.palette.common.white,
                        "&:hover": {
                            // transform: "scale(1.06)",
                            bgcolor: preOrder ? theme.palette.secondary.dark : theme.palette.primary.dark,
                        },
                    }}
                >

                        <FaIcon faIcon = "fa-plus" fontSize = {{md: 22, xs: isBigButton ? 22 : 18}} variant = "regular"
                            color = {theme.palette.common.white} />
                </IconButton >
            </Box >
        </Stack >
    );
};

export default memo(PurchasingControl);
